import { useState } from "react";
import { PlusIcon, XIcon } from "lucide-react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { getScreenFromPathname, mixpanelUtils } from "@/lib/mixpanel/mixpanel";

export const FormArrayInput = <T extends Record<string, unknown>>({
  fieldName,
  form,
  inputType = "text",
  label,
  placeholder,
}: {
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  inputType?: "text" | "number";
  label?: string;
  placeholder?: string;
}) => {
  const [currentValue, setCurrentValue] = useState("");

  const render = () => {
    return (
      <FormField
        control={form.control}
        name={fieldName}
        render={({ field }) => {
          // The `?? []` fallback is required because without it, submitting the
          // edit company profile form with values in NAICS codes would fail with
          // "naics form value is undefined" and prevent form submission. This
          // handles the case where field.value is undefined before form initialization.
          const value = (field.value as string[] | undefined) ?? [];

          const handleAdd = () => {
            const trimmedValue = currentValue.trim();
            if (trimmedValue) {
              mixpanelUtils.trackButtonClick(
                "Add array item",
                getScreenFromPathname(location.pathname),
              );
              field.onChange([...value, trimmedValue]);
              setCurrentValue("");
            }
          };

          const handleRemove = (index: number) => {
            mixpanelUtils.trackButtonClick(
              "Remove array item",
              getScreenFromPathname(location.pathname),
            );
            field.onChange(value.filter((_, i) => i !== index));
          };

          const handleKeyDown = (e: React.KeyboardEvent) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAdd();
            }
          };

          return (
            <FormItem className="text-slate-700">
              {label && <FormLabel>{label}</FormLabel>}
              <FormControl>
                <Column className="gap-2">
                  <Row className="gap-2">
                    <Input
                      onChange={(e) => {
                        setCurrentValue(e.target.value);
                      }}
                      onKeyDown={handleKeyDown}
                      placeholder={placeholder}
                      type={inputType}
                      value={currentValue}
                    />
                    <Button onClick={handleAdd} type="button" variant="outline">
                      <PlusIcon className="h-4 w-4" />
                      Add
                    </Button>
                  </Row>
                  {value.length > 0 && (
                    <Row className="flex-wrap gap-2">
                      {value.map((item, index) => (
                        <Badge
                          className="gap-1 border-primary-900 bg-primary-400"
                          key={index}
                        >
                          {item}
                          <XIcon
                            className="h-4 w-4 cursor-pointer"
                            onClick={() => {
                              handleRemove(index);
                            }}
                          />
                        </Badge>
                      ))}
                    </Row>
                  )}
                </Column>
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
    );
  };

  return render();
};
